import type * as graphql from 'graphql'
import * as rxjs from 'rxjs'

import { GraphqlResultError } from './graphql-result-error'

/**
 * Default time before a pending GraphQL request is reported as slow.
 *
 * This does not wait for a failure; it lets UI show lightweight feedback while
 * the first request is still in flight.
 */
const DEFAULT_SLOW_AFTER_MS = 15e3

/**
 * Default retry plan for retry-enabled GraphQL requests.
 *
 * Each entry schedules one retry, so the default means one initial request
 * plus two retries: one after 1500ms and one after 3000ms. That gives quick
 * failures a real recovery window before the UI reports that loading failed.
 */
const DEFAULT_RETRY_DELAYS_MS = [1500, 3000] as const

/**
 * Internal event bus for GraphQL retry lifecycle notifications.
 *
 * Consumers should subscribe to `retryRequest.events$`; keeping the subject
 * private prevents callers from emitting fake retry events.
 */
const eventsSubject = new rxjs.Subject<retryRequest.Event>()

/**
 * Applies the lightweight GraphQL retry behavior to a cold request observable.
 *
 * The default retries transport-style failures when retry is enabled for the
 * operation. GraphQL `errors[]` results are skipped by default because they are
 * deterministic application responses, not transient request failures.
 */
export function retryRequest<T>(
  request$: rxjs.Observable<T>,
  params: retryRequest.Params,
): rxjs.Observable<T> {
  if (!RequestLifecycle.isEnabled(params)) {
    return request$
  }

  return rxjs.defer(() => {
    const lifecycle = new RequestLifecycle(params)
    const slowSubscription = lifecycle.startSlowTimer()

    return request$.pipe(
      rxjs.retry({
        count: lifecycle.retryCount,
        delay: lifecycle.retry,
      }),
      rxjs.tap({
        complete: lifecycle.recovered,
        error: lifecycle.failed,
      }),
      rxjs.finalize(() => {
        slowSubscription?.unsubscribe()
        lifecycle.cancel()
      }),
    )
  })
}
export namespace retryRequest {
  /**
   * Retry configuration accepted by `executeGql` and `createExecutor`.
   *
   * Use `false` to disable retries completely for an operation. Provide an
   * object to configure retry behavior and opt mutations into retry.
   */
  export type Config = false | Policy

  /**
   * Controls retry timing and notification behavior for one GraphQL operation.
   */
  export interface Policy {
    /**
     * Emits a `slow` event if the request has not settled after this many
     * milliseconds.
     */
    slowAfterMs?: number
    /**
     * Retry delays. Each entry schedules one retry attempt, so the array length
     * controls how many retries can happen.
     */
    retryDelaysMs?: readonly number[]
    /**
     * User-facing message displayed while the request is slow or retrying.
     */
    message?: string
    /**
     * Optional operation-specific retry decision. Returning `false` stops retry
     * for the current failure; returning `true` can opt known-safe GraphQL result
     * errors back into retry.
     */
    retryWhen?: (params: Policy.DecisionParams) => boolean
  }
  export namespace Policy {
    /**
     * Context passed to `retryWhen` for the current failed attempt.
     */
    export interface DecisionParams {
      /**
       * The failed attempt number, starting at `1`.
       */
      readonly attempt: number
      readonly error: unknown
      /**
       * Total possible attempts, derived from `retryDelaysMs.length + 1`.
       */
      readonly maxAttempts: number
      readonly operationName?: string
      readonly operationType?: graphql.OperationTypeNode
    }
  }

  /**
   * Retry lifecycle event emitted for UI feedback.
   */
  export type Event =
    | Event.Slow
    | Event.Retrying
    | Event.Recovered
    | Event.Failed
  export namespace Event {
    /**
     * Fields shared by all retry lifecycle events.
     */
    export interface Base {
      readonly attempt: number
      /**
       * Total possible attempts, derived from `retryDelaysMs.length + 1`.
       */
      readonly maxAttempts: number
      readonly message?: string
      readonly operationName?: string
      readonly operationType?: graphql.OperationTypeNode
      readonly requestId: string
    }

    /**
     * The request has not failed yet, but it has exceeded `slowAfterMs`.
     */
    export interface Slow extends Base {
      readonly type: 'slow'
    }

    /**
     * The request failed and a retry has been scheduled.
     */
    export interface Retrying extends Base {
      readonly nextRetryDelayMs: number
      readonly error: unknown
      readonly type: 'retrying'
    }

    /**
     * A previously slow/retrying request completed or was cancelled.
     */
    export interface Recovered extends Base {
      readonly type: 'recovered'
    }

    /**
     * Retries ended and the request still failed.
     */
    export interface Failed extends Base {
      readonly error: unknown
      readonly type: 'failed'
    }
  }

  /**
   * Internal metadata used to identify retry events for one request.
   */
  export interface Params {
    readonly operationName?: string
    readonly operationType?: graphql.OperationTypeNode
    readonly retry?: Config
  }

  /**
   * Emits slow/retry lifecycle events for GraphQL requests.
   */
  export const events$ = eventsSubject.asObservable()
}

/**
 * Tracks retry lifecycle state for one subscription to a GraphQL request.
 *
 * Each subscription needs its own request id and event state because the same
 * cold request observable can be subscribed to multiple times.
 */
class RequestLifecycle {
  /**
   * Monotonic suffix used to create request ids for retry lifecycle events.
   *
   * The id lets UI correlate `slow`, `retrying`, `recovered`, and `failed`
   * events for the same request, even when several GraphQL requests overlap.
   */
  private static _requestIdCounter = 0

  /**
   * Creates the correlation id shared by all lifecycle events for one request.
   */
  private static _createRequestId(): string {
    return `graphql-request-${Date.now()}-${RequestLifecycle._requestIdCounter++}`
  }

  /**
   * Queries retry by default; mutations retry only when given a config object.
   */
  public static isEnabled({
    operationType,
    retry,
  }: retryRequest.Params): boolean {
    return (
      retry !== false && (retry !== undefined || operationType !== 'mutation')
    )
  }

  /**
   * Operation name included in retry lifecycle events when it is known.
   */
  private readonly _operationName?: string
  /**
   * Operation type controls the default retry behavior.
   */
  private readonly _operationType?: graphql.OperationTypeNode
  /**
   * Correlates slow/retry/recovered/failed events for this subscription.
   */
  private readonly _requestId = RequestLifecycle._createRequestId()
  /**
   * Operation-level retry config after `false` has been handled.
   */
  private readonly _retry?: retryRequest.Policy
  /**
   * Current attempt number reported in lifecycle events.
   */
  private _attempt = 1

  /**
   * Whether this request has already produced UI-visible feedback.
   */
  private _hasVisibleEvent = false

  /**
   * Prevents duplicate terminal events from completion and finalization.
   */
  private _isSettled = false

  public constructor({
    operationName,
    operationType,
    retry,
  }: retryRequest.Params) {
    this._operationName = operationName
    this._operationType = operationType
    this._retry = retry === false ? undefined : retry
  }

  public get retryCount(): number {
    return this._retryDelaysMs.length
  }

  /**
   * Starts the timer that reports a request as slow before it has failed.
   */
  public startSlowTimer(): rxjs.Subscription | undefined {
    return this._slowAfterMs > 0
      ? rxjs.timer(this._slowAfterMs).subscribe(() => {
          if (!this._hasVisibleEvent && !this._isSettled) {
            // This fires before an error when the initial request is simply
            // taking longer than expected.
            this._emitVisible({ type: 'slow' })
          }
        })
      : undefined
  }

  /**
   * RxJS retry callback that decides whether to wait and try again.
   */
  public readonly retry = (
    error: unknown,
    retryCount: number,
  ): rxjs.Observable<unknown> => {
    if (!this._shouldRetry(error, retryCount)) {
      return rxjs.throwError(() => error)
    }

    this._attempt = retryCount + 1
    const nextRetryDelayMs = this._getNextRetryDelayMs(retryCount)

    this._emitVisible({
      error,
      nextRetryDelayMs,
      type: 'retrying',
    })

    return rxjs.timer(nextRetryDelayMs)
  }

  /**
   * Marks a visible slow/retrying request as successfully settled.
   */
  public readonly recovered = (): void => {
    this._settle({ type: 'recovered' })
  }

  /**
   * Marks a visible slow/retrying request as failed after retries ended.
   */
  public readonly failed = (error: unknown): void => {
    this._settle({ error, type: 'failed' })
  }

  /**
   * Cleans up a visible request if the subscription is cancelled.
   */
  public readonly cancel = (): void => {
    if (!this._isSettled) {
      // Treat cancellation after a visible slow/retry event as recovered so
      // sticky UI feedback does not remain on screen.
      this._settle({ type: 'recovered' })
    }
  }

  /**
   * Adds request lifecycle fields to an event-specific payload.
   */
  private _createEvent<TDetails extends RequestLifecycle.EventDetails>(
    details: TDetails,
  ): retryRequest.Event.Base & TDetails {
    const base: retryRequest.Event.Base = {
      attempt: this._attempt,
      maxAttempts: this._maxAttempts,
      message: this._message,
      operationName: this._operationName,
      operationType: this._operationType,
      requestId: this._requestId,
    }

    return { ...base, ...details }
  }

  /**
   * Emits a UI-visible event and remembers that terminal events matter.
   */
  private _emitVisible(details: RequestLifecycle.VisibleEventDetails): void {
    this._hasVisibleEvent = true
    eventsSubject.next(this._createEvent(details))
  }

  /**
   * Emits the terminal lifecycle event only after visible retry feedback.
   */
  private _settle(details: RequestLifecycle.SettledEventDetails): void {
    this._isSettled = true
    if (this._hasVisibleEvent) {
      eventsSubject.next(this._createEvent(details))
    }
  }

  /**
   * Applies the caller's retry override, defaulting to retrying transport-like
   * failures.
   */
  private _shouldRetry(error: unknown, retryCount: number): boolean {
    const retryDecisionParams: retryRequest.Policy.DecisionParams = {
      attempt: retryCount,
      error,
      maxAttempts: this._maxAttempts,
      operationName: this._operationName,
      operationType: this._operationType,
    }

    const retryDecision = this._retry?.retryWhen?.(retryDecisionParams)
    if (retryDecision !== undefined) {
      return retryDecision
    }

    return !(error instanceof GraphqlResultError)
  }

  private get _retryDelaysMs(): readonly number[] {
    return this._retry?.retryDelaysMs ?? DEFAULT_RETRY_DELAYS_MS
  }

  private get _maxAttempts(): number {
    return this.retryCount + 1
  }

  private get _message(): string | undefined {
    return this._retry?.message
  }

  private get _slowAfterMs(): number {
    return Math.max(0, this._retry?.slowAfterMs ?? DEFAULT_SLOW_AFTER_MS)
  }

  /**
   * Reads the delay for the current retry attempt.
   */
  private _getNextRetryDelayMs(retryCount: number): number {
    return Math.max(0, this._retryDelaysMs[retryCount - 1] ?? 0)
  }
}

namespace RequestLifecycle {
  export type EventDetails = {
    [Type in retryRequest.Event['type']]: Omit<
      Extract<retryRequest.Event, { readonly type: Type }>,
      keyof retryRequest.Event.Base
    >
  }[retryRequest.Event['type']]
  export type VisibleEventDetails = Extract<
    EventDetails,
    { readonly type: 'slow' | 'retrying' }
  >
  export type SettledEventDetails = Extract<
    EventDetails,
    { readonly type: 'failed' | 'recovered' }
  >
}
